import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";
import { SEO, breadcrumbJsonLd, itemListJsonLd } from "../components/SEO";
import { Layout, Breadcrumbs } from "../components/Layout";
import { NotListedCTA } from "../components/NotListedCTA";
import { RelatedGuides } from "../components/RelatedGuides";
import { useCatalogLocale } from "../lib/localizeCatalog";
import { lp } from "../lib/urls";

export default function ModelDetail() {
  const { brandSlug, modelSlug } = useParams();
  const { t } = useTranslation();
  const loc = useCatalogLocale();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null);
    setNotFound(false);
    api
      .get(`/models/${brandSlug}/${modelSlug}`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true));
  }, [brandSlug, modelSlug]);

  if (notFound)
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-6 py-24 text-center">
          <h1 className="font-display font-bold text-3xl">
            {t("model2.notFound")}
          </h1>
          <Link to={lp("/brands")} className="underline mt-4 inline-block">
            {t("cat.back")}
          </Link>
        </div>
      </Layout>
    );

  if (!data)
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-6 py-24 text-muted-foreground">
          {t("cat.loading")}
        </div>
      </Layout>
    );

  const { model, parts } = data;
  const crumbs = [
    { name: t("qa2.home"), path: "/" },
    { name: t("cat.brands"), path: "/brands" },
    { name: model.brand_name, path: `/brand/${brandSlug}` },
    { name: model.name },
  ];

  return (
    <Layout>
      <SEO
        title={t("seo.modelTitle", {
          brand: model.brand_name,
          model: model.name,
        })}
        description={(() => {
          const txt = t("seo.modelText", { brand: model.brand_name, model: model.name });
          return txt.length > 160 ? txt.slice(0, 157).replace(/\s+\S*$/, "") + "…" : txt;
        })()}
        path={`/brand/${brandSlug}/${modelSlug}`}
        jsonLd={[
          breadcrumbJsonLd([
            { name: t("qa2.home"), path: "/" },
            { name: "Brands", path: "/brands" },
            { name: model.brand_name, path: `/brand/${brandSlug}` },
            { name: model.name, path: `/brand/${brandSlug}/${modelSlug}` },
          ]),
          itemListJsonLd(
            `${model.brand_name} ${model.name} undercarriage parts`,
            parts.map((p) => ({
              name: `${model.brand_name} ${model.name} ${p.name}`,
              path: `/part/${p.slug}`,
            })),
          ),
        ]}
      />
      <Breadcrumbs items={crumbs} />
      <div className="p-6 sm:p-14 border-b border-border bg-white">
        <p className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-gray-500 mb-3">
          // {loc.machType(model.machinery_type)} / {model.brand_name}
        </p>
        <h1
          className="font-display font-black uppercase text-4xl sm:text-5xl tracking-tighter mb-4 text-gray-950"
          data-testid="model-title"
        >
          {t("seo.modelTitle", { brand: model.brand_name, model: model.name })}
        </h1>
        <p className="text-gray-600 max-w-3xl">
          {t("model2.intro", { brand: model.brand_name, model: model.name })}
        </p>
        <NotListedCTA
          question={t("brand2.partNotListed")}
          cta={t("brand2.requestPricing")}
          to={lp(
            `/request-quote?brand=${encodeURIComponent(model.brand_name)}&model=${encodeURIComponent(model.name)}`,
          )}
          testid="model-quote-hint"
        />
      </div>

      <div className="overflow-x-auto border-b border-border">
        <table className="w-full text-sm bg-white" data-testid="parts-table">
          <thead>
            <tr className="bg-[#0A0A0A]">
              <th className="text-left px-6 sm:px-14 py-3 font-mono-tech text-[10px] font-bold uppercase tracking-[0.25em] text-yellow-500">
                {t("model2.thName")}
              </th>
              <th className="text-left px-4 py-3 font-mono-tech text-[10px] font-bold uppercase tracking-[0.25em] text-yellow-500">
                {t("model2.thBrand")}
              </th>
              <th className="text-left px-4 py-3 font-mono-tech text-[10px] font-bold uppercase tracking-[0.25em] text-yellow-500">
                {t("model2.thType")}
              </th>
              <th className="text-left px-4 py-3 font-mono-tech text-[10px] font-bold uppercase tracking-[0.25em] text-yellow-500">
                {t("model2.thModel")}
              </th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              const rtCount = parts.filter((p) => p.name === "Rubber track").length;
              let rtShown = false;
              return parts
                .filter((p) => {
                  if (p.name !== "Rubber track" || rtCount < 2) return true;
                  if (rtShown) return false;
                  rtShown = true;
                  return true;
                })
                .map((p, i) => (
              <tr
                key={p.id}
                className={`border-b border-border hover:bg-yellow-50 transition-colors ${i % 2 === 1 ? "bg-gray-50" : ""}`}
              >
                <td className="p-0">
                  <Link
                    to={lp(`/part/${p.slug}`)}
                    data-testid={`part-row-${p.slug}`}
                    className="block px-6 sm:px-14 py-4 font-semibold text-gray-950 hover:underline underline-offset-4"
                  >
                    {loc.partName(p.name)}
                    {p.name === "Rubber track" && rtCount > 1 && (
                      <span className="ml-2 font-mono-tech font-normal text-[10px] uppercase tracking-wider bg-yellow-500 text-black px-1.5 py-0.5" data-testid="rt-sizes-badge">
                        {rtCount} {t("cat.sizesWord")}
                      </span>
                    )}
                    {loc.partNameEn(p.name) && (
                      <span className="block font-mono-tech font-normal text-[10px] uppercase tracking-wider text-gray-400 mt-0.5 no-underline">
                        {loc.partNameEn(p.name)}
                      </span>
                    )}
                  </Link>
                </td>
                <td className="px-4 py-4">
                  <Link
                    to={lp(`/part/${p.slug}`)}
                    className="block text-gray-600"
                  >
                    {p.brand_name}
                  </Link>
                </td>
                <td className="px-4 py-4">
                  <Link
                    to={lp(`/part/${p.slug}`)}
                    className="block text-gray-600"
                  >
                    {loc.machType(p.machinery_type)}
                  </Link>
                </td>
                <td className="px-4 py-4">
                  <Link
                    to={lp(`/part/${p.slug}`)}
                    className="block font-mono-tech text-xs text-gray-950"
                  >
                    {p.model_name}
                  </Link>
                </td>
              </tr>
                ));
            })()}
          </tbody>
        </table>
      </div>

      <section
        className="border-t border-border bg-gray-50 p-6 sm:px-14 sm:py-10"
        data-testid="model-seo-text"
      >
        <p className="text-sm text-gray-600 leading-relaxed max-w-4xl">
          {parts.length > 0 && parts.every((p) => p.type_key === "rubber-track")
            ? t("seo.modelTextRT", { brand: model.brand_name, model: model.name })
            : t("seo.modelText", { brand: model.brand_name, model: model.name })}
        </p>
      </section>
      <RelatedGuides />
    </Layout>
  );
}
